import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  ArrowLeftRight,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  Filter,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import {
  timeOffService,
  TimeOffRequest,
  ShiftSwapRequest,
  RequestStatus,
} from '../../services/time-off.service';

export default function AdminReviewPage() {
  const user = useAuthStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive active tab directly from URL (single source of truth)
  const urlTab = searchParams.get('tab');
  const activeTab: 'time-off' | 'shift-swaps' = urlTab === 'shift-swaps' ? 'shift-swaps' : 'time-off';

  // Handler to change tabs - directly updates URL
  const handleTabChange = (tab: 'time-off' | 'shift-swaps') => {
    setSearchParams({ tab }, { replace: true });
  };

  const [statusFilter, setStatusFilter] = useState<RequestStatus | 'ALL'>('PENDING');
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [shiftSwapRequests, setShiftSwapRequests] = useState<ShiftSwapRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user?.organizationId, statusFilter]);

  const loadData = async () => {
    if (!user?.organizationId) return;

    setIsLoading(true);
    try {
      const filterStatus = statusFilter === 'ALL' ? undefined : statusFilter;
      const [timeOffData, shiftSwapData] = await Promise.all([
        timeOffService
          .getAllTimeOffRequests(user.organizationId, filterStatus)
          .catch(() => getDemoTimeOffRequests()),
        timeOffService
          .getAllShiftSwapRequests(user.organizationId, filterStatus)
          .catch(() => getDemoShiftSwapRequests()),
      ]);

      setTimeOffRequests(timeOffData);
      setShiftSwapRequests(shiftSwapData);
    } finally {
      setIsLoading(false);
    }
  };

  const getDemoTimeOffRequests = (): TimeOffRequest[] => {
    return [
      {
        id: 'req-001',
        agentId: 'agent-001',
        agentName: 'Sarah Chen',
        teamName: 'Phone Support',
        type: 'VACATION',
        startDate: '2026-02-10',
        endDate: '2026-02-14',
        totalDays: 5,
        reason: 'Family vacation',
        status: 'PENDING',
        requestedAt: '2026-01-08T10:30:00Z',
        scheduleImpact: {
          affectedShifts: 5,
          coverageGap: true,
          understaffedHours: 8,
          channelsAffected: ['Phone Support'],
          skillsRequired: ['Customer Service', 'Billing'],
        },
      },
      {
        id: 'req-002',
        agentId: 'agent-002',
        agentName: 'Marcus Johnson',
        teamName: 'Technical Support',
        type: 'SICK',
        startDate: '2026-01-20',
        endDate: '2026-01-21',
        totalDays: 2,
        status: 'PENDING',
        requestedAt: '2026-01-09T08:00:00Z',
        scheduleImpact: {
          affectedShifts: 2,
          coverageGap: false,
          understaffedHours: 0,
          channelsAffected: ['Live Chat'],
          skillsRequired: ['Technical Support'],
        },
      },
      {
        id: 'req-003',
        agentId: 'agent-003',
        agentName: 'Emily Rodriguez',
        teamName: 'Phone Support',
        type: 'PERSONAL',
        startDate: '2026-01-25',
        endDate: '2026-01-25',
        totalDays: 1,
        status: 'PENDING',
        requestedAt: '2026-01-09T14:20:00Z',
        scheduleImpact: {
          affectedShifts: 1,
          coverageGap: false,
          understaffedHours: 0,
          channelsAffected: ['Email Support'],
          skillsRequired: [],
        },
      },
    ];
  };

  const getDemoShiftSwapRequests = (): ShiftSwapRequest[] => {
    return [
      {
        id: 'swap-001',
        requestingAgentId: 'agent-004',
        requestingAgentName: 'David Kim',
        targetAgentId: 'agent-005',
        targetAgentName: 'Jessica Taylor',
        requestingShiftId: 'shift-001',
        targetShiftId: 'shift-101',
        requestingShiftDate: '2026-01-18',
        targetShiftDate: '2026-01-19',
        requestingShiftTime: '09:00-17:00',
        targetShiftTime: '13:00-21:00',
        reason: 'Medical appointment',
        status: 'PENDING',
        requestedAt: '2026-01-09T11:00:00Z',
        targetAgentApproved: true,
        targetAgentReviewedAt: '2026-01-09T12:00:00Z',
        scheduleImpact: {
          affectedShifts: 2,
          coverageGap: false,
          understaffedHours: 0,
          channelsAffected: [],
          skillsRequired: [],
        },
      },
    ];
  };

  const handleReviewTimeOff = async (requestId: string, approved: boolean) => {
    try {
      await timeOffService.reviewTimeOffRequest(requestId, {
        status: approved ? 'APPROVED' : 'DENIED',
        reviewNotes: approved ? 'Approved' : 'Request denied',
      });
      await loadData();
    } catch (error) {
      console.error('Failed to review request:', error);
      alert('Failed to review request. Please try again.');
    }
  };

  const handleReviewShiftSwap = async (requestId: string, approved: boolean) => {
    try {
      await timeOffService.reviewShiftSwapRequest(requestId, {
        status: approved ? 'APPROVED' : 'DENIED',
        reviewNotes: approved ? 'Approved' : 'Swap denied',
      });
      await loadData();
    } catch (error) {
      console.error('Failed to review request:', error);
      alert('Failed to review request. Please try again.');
    }
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'DENIED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const pendingTimeOff = timeOffRequests.filter((r) => r.status === 'PENDING').length;
  const pendingShiftSwaps = shiftSwapRequests.filter((r) => r.status === 'PENDING').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Request Reviews</h1>
        <p className="text-gray-600 mt-2">
          Review and approve time off and shift swap requests
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Time Off</p>
              <p className="text-3xl font-bold text-yellow-600">{pendingTimeOff}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Shift Swaps</p>
              <p className="text-3xl font-bold text-purple-600">{pendingShiftSwaps}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <ArrowLeftRight className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Pending</p>
              <p className="text-3xl font-bold text-blue-600">
                {pendingTimeOff + pendingShiftSwaps}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Filter className="h-5 w-5 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RequestStatus | 'ALL')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="DENIED">Denied</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => handleTabChange('time-off')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'time-off'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Time Off Requests ({timeOffRequests.length})</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('shift-swaps')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'shift-swaps'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <ArrowLeftRight className="h-4 w-4" />
              <span>Shift Swaps ({shiftSwapRequests.length})</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Time Off Requests */}
      {activeTab === 'time-off' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : timeOffRequests.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
              <p className="text-gray-600">No time off requests match the selected filter</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {timeOffRequests.map((request) => (
                <div key={request.id} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{request.agentName}</h3>
                        <p className="text-sm text-gray-600">{request.teamName}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded">
                        {request.type}
                      </span>
                      <span
                        className={`px-3 py-1 text-sm font-medium rounded border ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {request.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6 mb-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Dates Requested</p>
                      <p className="font-medium text-gray-900">
                        {new Date(request.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}{' '}
                        -{' '}
                        {new Date(request.endDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                      <p className="text-sm text-gray-600">
                        {request.totalDays} day{request.totalDays !== 1 ? 's' : ''}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Requested</p>
                      <p className="font-medium text-gray-900">
                        {new Date(request.requestedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>

                  {request.reason && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Reason</p>
                      <p className="text-gray-900">{request.reason}</p>
                    </div>
                  )}

                  {/* Schedule Impact */}
                  {request.scheduleImpact && (
                    <div
                      className={`p-4 rounded-lg border mb-4 ${
                        request.scheduleImpact.coverageGap
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <AlertTriangle
                          className={`h-5 w-5 mt-0.5 ${
                            request.scheduleImpact.coverageGap
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-2">Schedule Impact</h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• {request.scheduleImpact.affectedShifts} shifts affected</li>
                            {request.scheduleImpact.understaffedHours > 0 && (
                              <li className="text-yellow-700">
                                • {request.scheduleImpact.understaffedHours} hours may be
                                understaffed
                              </li>
                            )}
                            <li>
                              • Channels: {request.scheduleImpact.channelsAffected.join(', ')}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {request.status === 'PENDING' && (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleReviewTimeOff(request.id, true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleReviewTimeOff(request.id, false)}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                      >
                        <XCircle className="h-4 w-4" />
                        <span>Deny</span>
                      </button>
                    </div>
                  )}

                  {request.reviewedAt && (
                    <div className="text-sm text-gray-600">
                      Reviewed by {request.reviewerName} on{' '}
                      {new Date(request.reviewedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shift Swap Requests */}
      {activeTab === 'shift-swaps' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : shiftSwapRequests.length === 0 ? (
            <div className="p-12 text-center">
              <ArrowLeftRight className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
              <p className="text-gray-600">No shift swap requests match the selected filter</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {shiftSwapRequests.map((request) => (
                <div key={request.id} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <ArrowLeftRight className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Shift Swap Request</h3>
                        <p className="text-sm text-gray-600">
                          {request.requestingAgentName} ↔ {request.targetAgentName}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 text-sm font-medium rounded border ${getStatusColor(
                        request.status
                      )}`}
                    >
                      {request.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-900 font-medium mb-2">
                        {request.requestingAgentName} gives:
                      </p>
                      <p className="text-gray-900">
                        {new Date(request.requestingShiftDate).toLocaleDateString()}
                      </p>
                      <p className="text-gray-600 text-sm">{request.requestingShiftTime}</p>
                    </div>
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <p className="text-sm text-green-900 font-medium mb-2">
                        {request.targetAgentName} gives:
                      </p>
                      <p className="text-gray-900">
                        {new Date(request.targetShiftDate).toLocaleDateString()}
                      </p>
                      <p className="text-gray-600 text-sm">{request.targetShiftTime}</p>
                    </div>
                  </div>

                  {request.reason && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Reason</p>
                      <p className="text-gray-900">{request.reason}</p>
                    </div>
                  )}

                  <div className="mb-4 text-sm">
                    <p className="text-gray-600">
                      Requested:{' '}
                      {new Date(request.requestedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                    {request.targetAgentApproved && (
                      <p className="text-green-600 font-medium mt-1">
                        ✓ {request.targetAgentName} approved
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {request.status === 'PENDING' && request.targetAgentApproved && (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleReviewShiftSwap(request.id, true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>Approve Swap</span>
                      </button>
                      <button
                        onClick={() => handleReviewShiftSwap(request.id, false)}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                      >
                        <XCircle className="h-4 w-4" />
                        <span>Deny Swap</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
